import { CommonModule, DatePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import htmlToPdfmake from 'html-to-pdfmake';
import pdfMake from 'pdfmake/build/pdfmake';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { Subject } from 'rxjs';
import { QuestionFile, ResponseData } from '../questionnaires.interface';
import { CaseDetailModel } from '../../../../insights.interface';
import { OrganizationModel } from '../../../../../organizations/organization.interface';
import { AnswerType } from '../../../../../manage-settings/manage-settings.enums';

export interface PrintQuestionnariesData {
  questionnairesData: ResponseData[];
  logo: string;
  attachmentName: string;
  caseDetail?: CaseDetailModel;
  orgDetail: OrganizationModel;
  uploadedFiles: QuestionFile[];
}

@Component({
  selector: 'app-print-questionnaires-form',
  standalone: true,
  imports: [CommonModule, DatePipe],
  templateUrl: './print-questionnaires-form.component.html',
})
export class PrintQuestionnairesFormComponent implements OnDestroy {
  printData: PrintQuestionnariesData | null = null;
  answerType = AnswerType;
  @Input() set questionnaireData(val: PrintQuestionnariesData) {
    this.initialize(val);
  }
  @Output() questionnariePdf = new EventEmitter<File>();

  private async initialize(val: PrintQuestionnariesData) {
    this.printData = val;
    this.cd.detectChanges();
    this.initialized = true;
  }

  @ViewChild('pdfContent', { static: true }) pdfContent!: ElementRef;
  initialized = false;
  private readonly destroy$ = new Subject<void>();
  constructor(private readonly cd: ChangeDetectorRef) {}
  async printPDF() {
    if (this.printData) {
      const pdfContent = document.getElementById('pdf-data');
      if (pdfContent) {
        let pdfMakeContent = htmlToPdfmake(pdfContent.innerHTML);
        if (Array.isArray(pdfMakeContent)) {
          /* eslint-disable @typescript-eslint/no-explicit-any */
          pdfMakeContent = pdfMakeContent.map((contentItem: any) => {
            return {
              ...contentItem,
              pageBreak: 'avoid',
              margin: [0, 10, 0, 10],
            };
          });
        }

        const documentDefinition: TDocumentDefinitions = {
          content: pdfMakeContent,
          pageMargins: [20, 20, 20, 20],
          footer: function (currentPage: number) {
            return {
              columns: [
                {
                  text: ``,
                  alignment: 'left',
                },
                {
                  text: `Powered by Insights to Behavior®`,
                  alignment: 'center',
                },
                {
                  text: `${currentPage}`,
                  alignment: 'right',
                },
              ],
              margin: [15, 0, 10, 0],
              color: '#475467',
            };
          },
          styles: {
            questionContainer: {
              margin: [0, 10, 0, 10],
            },
            questionHeader: {
              marginBottom: 5,
              bold: true,
              fontSize: 14,
            },
            stepIndicator: {
              fillColor: '#d1f7d6',
              color: '#22c55e',
              fontSize: 14,
              margin: [0, 0, 10, 0],
              alignment: 'left',
            },
            questionText: {
              fontSize: 12,
              marginBottom: 3,
              color: '#475467',
            },
            datalabel: {
              lineHeight: 1.5,
              color: '#475467',
            },
            dataValue: {
              color: '#475467',
            },
            columns: {
              columnGap: 7,
            },
            chartImage: {
              alignment: 'center',
            },
          },
          defaultStyle: {
            columnGap: 10,
            fontSize: 12,
          },
        };

        pdfMake.createPdf(documentDefinition).getBase64(base64 => {
          const byteCharacters = atob(base64);
          const byteNumbers = Array.from(byteCharacters, c => c.charCodeAt(0));
          const byteArray = new Uint8Array(byteNumbers);

          const file = new File(
            [byteArray],

            `${this.printData?.attachmentName}.pdf`,
            { type: 'application/pdf' }
          );
          this.questionnariePdf.emit(file);
        });
        this.initialized = false;
        this.ngOnDestroy();
      }
    }
  }

  convertToDate(timeString: string): Date {
    const [hours, minutes] = timeString.split(':').map(Number);
    const currentDate = new Date();
    currentDate.setHours(hours, minutes, 0, 0);

    return currentDate;
  }

  getQuestionFileList(questionId: number): QuestionFile[] {
    const fileData = this.printData?.uploadedFiles.filter(
      f => f.questionId === questionId
    );
    return fileData ?? [];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
